import { useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import BaseAppHelmet from '../components/Base/BaseAppHelmet'
import CssBaseline from '@material-ui/core/CssBaseline'
import Layout from '../components/ConsumerCheckout/Layout/Layout'
import axios from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import {
    AUTH_FAILED,
    DUPLICATE_ORDER,
    ORDER_CANCELLED,
    ORDER_CANCELLED_F,
    PAYMENT_METHOD,
    PROMO_CODE_ALREADY_USED,
} from '../components/ConsumerCheckout/ConsumerCheckoutConstants'
import GcgCircularProgress from '../components/Loaders/GcgCircularProgress'
import isEmpty from 'lodash/isEmpty'
import has from 'lodash/has'
import Grid from '@material-ui/core/Grid'

import {
    addFormErrors,
    collectFormErrors,
    removeErrorFromPath,
    removeErrorFromPaths,
} from '../utils/formErrorUtils'
import OrderSummary from '../components/ConsumerCheckout/Components/OrderSummary'
import LargeGcgCircularProgress from '../components/Loaders/LargeGcgCircularProgress'
import Cookies from 'js-cookie'
import Alert from '@material-ui/lab/Alert'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import Collapse from '@material-ui/core/Collapse'
import { pushToDataLayer } from '../utils/dataLayer'
import {
    buildTriplePixelLineItemsFromGiftCards,
    fireTriplePixelContact,
    fireTriplePixelPurchase,
} from '../utils/triplePixel'
import useFetchCart from '../hooks/useFetchCart'
import useFetchUserAndPaymentMethods from '../hooks/useFetchUserAndPaymentMethods'
import useUpdateCart from '../hooks/useUpdateCart'
import useCreateOrder from '../hooks/useCreateOrder'
import useCreatePaypalOrder from '../hooks/useCreatePaypalOrder'
import {
    ERROR_AUTHORIZING_PAYPAL_PAYMENT,
    ERROR_CREATING_PAYPAL_ORDER,
    ERROR_REDIRECTING_TO_ORDER_CONFIRMATION_PAGE,
    ERROR_SIGNING_UP_GUEST_USER,
} from '../errorConstants'
import * as Sentry from '@sentry/react'
import { CONSUMER_CART_SOURCE, VISA } from '../constants'
import useApplyPromoCode from '../hooks/useApplyPromoCode'
import useRemovePromoCode from '../hooks/useRemovePromoCode'
import useValidateVoucherifyPromoCode from '../hooks/useValidateVoucherifyPromoCode'
import EmptyCart from '../components/ConsumerCheckout/Components/EmptyCart'
import ConsumerGuestCheckoutForm from '../components/ConsumerCheckout/Components/ConsumerGuestCheckoutForm'
import ConsumerCheckoutForm from '../components/ConsumerCheckout/Components/ConsumerCheckoutForm'
import SimpleBackdrop from '../components/Loaders/SimpleBackdrop'
import useFetchUserPaymentMethods from '../hooks/useFetchUserPaymentMethods'
import useLogPaypalFailureAuthorization from '../hooks/useLogPaypalFailureAuthorization'
import CheckoutSessionElements from '../components/ConsumerCheckout/CheckoutSessionElements'
import { getActiveCheckout } from '../components/ConsumerCheckout/checkoutSessionRuntime'
import { scroller } from 'react-scroll'
import {
    clearCheckoutSessionReturn,
    getCheckoutSessionReturnId,
    isCheckoutSessionReturn,
    readStashedCheckoutEmail,
    readStashedCheckoutPhone,
    readStashedCheckoutPromoCode,
    stashCheckoutEmail,
    stashCheckoutPhone,
    stashCheckoutPromoCode,
} from '../components/ConsumerCheckout/checkoutSessionReturn'
import { requestCheckoutSessionApproval } from '../components/ConsumerCheckout/checkoutSessionApproval'
import {
    getCheckoutSessionPayment,
    updateCheckoutSessionAmount,
} from '../api'
import { extractEmail, getCookie, maskPhone } from '../utility'
import ProcessingModal from '../components/Modals/ProcessingModal'
import ConsumerCheckoutLoginCreateAccountModal from '../components/Modals/ConsumerCheckoutLoginCreateAccountModal'
import {
    consumerBillingSchema,
    consumerGuestCheckoutBillingSchema,
} from '../components/formValidationSchemas'
import {
    applyVoucherifyPreviewToCart,
    removeVoucherifyPreviewFromCart,
    rememberVoucherifyPromoCode,
    forgetVoucherifyPromoCode,
    readVoucherifyPromoCode,
    markVoucherifyPromoCodeManuallyRemoved,
    hasManuallyRemovedVoucherifyPromoCode,
} from '../utils/voucherifyUtils'

// Listed the other way round on purpose: these six are Stripe's error types that the Payment Element
// draws nothing for, so they are the only ones we have to speak about. Naming the types it does draw
// would mean guessing which one a decline arrives as, and a wrong guess shows the message twice.
const ELEMENT_SILENT_ERROR_TYPES = [
    'api_connection_error',
    'api_error',
    'authentication_error',
    'idempotency_error',
    'invalid_request_error',
    'rate_limit_error',
]

// An error with no type is not Stripe's — it is a fault in our own code, and must never be swallowed.
const isSelfReportedByElement = (error) =>
    typeof error?.type === 'string' &&
    !ELEMENT_SILENT_ERROR_TYPES.includes(error.type)

const BILLING_DETAILS_EMPTY = {
    email: '',
    phone: '',
    address1: '',
    city: '',
    region: '',
    postalCode: '',
}

const axiosInstance = axios.create({
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
})

const useOptions = () => {
    return {
        hidePostalCode: true,
        disableLink: true,
        style: {
            base: {
                fontSize: '16px',
                letterSpacing: '0.025em',
                '::placeholder': {
                    color: '#bababa',
                },
            },
            invalid: {
                color: '#9e2146',
            },
        },
    }
}

const useStyles = makeStyles((theme) => ({
    consumerCheckoutContainer: {
        marginBottom: '1.25rem',
        [theme.breakpoints.up('sm')]: {
            marginTop: '1.25rem',
            padding: '0 10px 20px 10px',
        },
    },
    orderSummaryContainer: {
        position: 'relative',
        backgroundColor: '#F1F1F1',
        borderRadius: 5,
        fontSize: '0.98rem',
        padding: 20,
        height: '100%',
        margin: '1.25rem auto auto auto',
        [theme.breakpoints.up('md')]: {
            marginTop: 0,
            maxWidth: 'none',
        },
    },
    orderSummaryContainerLoading: {
        background: 'rgba(0, 0, 0, 0.5)',
        borderRadius: 5,
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 1,
        paddingTop: '20%',
        textAlign: 'center',
    },
    alertMessageContainer: {
        marginTop: '1.25rem',
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            marginTop: 0,
            marginBottom: '1rem',
        },
    },
    nortonAndBbbLogosContainer: {
        marginTop: '1.25rem',
    },
    nortonLogo: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    bbbLogo: {
        maxHeight: 56,
        marginTop: 7,
        marginLeft: 'auto',
        marginRight: 'auto',
    },
}))

const VIRTUAL_VISA_GIFTING_THRESHOLD = 1000000
const VIRTUAL_VISA_SELF_GIFTING_THRESHOLD = 1000000
const VIRTUAL_SELF_GIFTING_THRESHOLD = 1000000

function convertToTwilioFormat(phoneNumber) {
    return phoneNumber.replace(/\D/g, '')
}

const ConsumerCheckoutPage = (props) => {
    const classes = useStyles()
    // CON-9505: deliberately no useCheckout() here. This page renders on a redirect return too, when
    // there is no session to mount and therefore no provider above it — the hook would throw. The
    // mounted instance is published by CheckoutSessionPaymentFields and read at the moment it is needed.
    const options = useOptions()
    const queryClient = useQueryClient()
    const {
        baseUrl,
        brandLogoUrl,
        footerContent,
        facebookLoginUrl,
        googleLoginUrl,
        recaptchaEnabled,
        recaptchaSiteKey,
        deviceId,
        checkoutClientSecret,
        checkoutSessionLoadFailed,
        checkoutAppearance,
        onPaymentFormLoadError,
        onCreateCheckoutSession,
    } = props
    const isBlacklisted = !!window.React.isBlacklisted
    const blacklistedMessage = window.React.blacklistedMessage
    const quantityAdjustmentMessage = JSON.parse(window.React.formErrors) || []

    // TODO refactor as much state to a useReducer
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
    const [createGuestAccountError, setCreateGuestAccountError] =
        useState(false)
    const [errors, setErrors] = useState([])
    const [errorCode, setErrorCode] = useState(null)
    const [billingErrors, setBillingErrors] = useState([])
    const [signupFormErrors, setSignupFormErrors] = useState([])
    const [loginFormErrors, setLoginFormErrors] = useState([])
    const [processing, setProcessing] = useState(false)
    const [billingDetails, setBillingDetails] = useState(BILLING_DETAILS_EMPTY)
    const [cvv, setCvv] = useState('')
    // The element owns the saved-vs-new choice now, so the page starts on the card form and stays
    // there; PAYPAL is the one other value, set by the PayPal button's own onClick.
    const [paymentMethodOption, setPaymentMethodOption] = useState(
        PAYMENT_METHOD.NEW
    )
    const [showCreditCardForm, setShowCreditCardForm] = useState(true)
    const [showPhoneInput] = useState(true)
    const [showPlaceOrderButton, setShowPlaceOrderButton] = useState(true)
    const [showGuestCheckout, setShowGuestCheckout] = useState(false)
    const [authModalIsOpen, setAuthModalIsOpen] = useState(false)
    const [guestEmail, setGuestEmail] = useState('')
    const [userEmail, setUserEmail] = useState('')
    const [savePaymentMethod, setSavePaymentMethod] = useState(true)
    const [showQuantityAdjustmentMessage, setShowQuantityAdjustmentMessage] =
        useState(!isEmpty(quantityAdjustmentMessage))
    const [guestAccountCreated, setGuestAccountCreated] = useState(false)
    const [user, setUser] = useState({})
    const [anyPaymentMethodsExist, setAnyPaymentMethodsExist] = useState(false)
    const isPaypalCheckout = paymentMethodOption === PAYMENT_METHOD.PAYPAL
    let paypalId = null
    const [
        cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold,
        setCartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold,
    ] = useState(null)
    const [
        cartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold,
        setCartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold,
    ] = useState(null)
    const [
        cartHasVirtualVisaWithTotalBalanceMoreThanThreshold,
        setCartHasVirtualVisaWithTotalBalanceMoreThanThreshold,
    ] = useState(null)
    const [errorSelfGifting, setErrorSelfGifting] = useState(false)
    const [errorPhone, setErrorPhone] = useState(false)
    const [phoneCheckSuccess, setPhoneCheckSuccess] = useState(false)
    const [checkingPhone, setCheckingPhone] = useState(false)
    const [errorCvv, setErrorCvv] = useState(false)
    const [loadedPhoneFromUserObject, setLoadedPhoneFromUserObject] =
        useState(false)
    const [showStripe, setShowStripe] = useState(true)
    const [showCardError, setShowCardError] = useState(true)
    // Failure copy for a redirect return. Own state, not errors: the fresh load's lifecycle
    // (clearErrors, focus handlers, the provider re-key) wipes the shared error state.
    const [returnFailureMessage, setReturnFailureMessage] = useState('')
    const [selectedPaymentElement, setSelectedPaymentElement] = useState('') //for stripe elements only
    const voucherifyPromoCodesEnabled =
        window.React?.voucherify_promos_enabled === '1'
    const [voucherifyRemoveIsLoading, setVoucherifyRemoveIsLoading] =
        useState(false)
    const [lastAutoAppliedVoucherifyPromoCode, setLastAutoAppliedVoucherifyPromoCode] =
        useState(null)
    const cartItemsSignatureRef = useRef(null)
    const autoApplyVoucherInFlightRef = useRef(false)
    const lastTriplePixelPurchaseOrderIdRef = useRef(null)

    const handleErrorAlertClick = () => {
        setErrors([])
        setShowCardError(false)
    }

    const {
        mutateAsync: applyPromoCodeLegacy,
        isLoading: legacyApplyPromoIsLoading,
    } = useApplyPromoCode()

    const {
        mutateAsync: removePromoCodeLegacy,
        isLoading: legacyRemovePromoIsLoading,
    } = useRemovePromoCode()

    const {
        mutateAsync: validateVoucherifyPromoCode,
        isLoading: voucherifyApplyIsLoading,
    } = useValidateVoucherifyPromoCode()

    const applyPromoIsLoading = voucherifyPromoCodesEnabled
        ? voucherifyApplyIsLoading
        : legacyApplyPromoIsLoading
    const removePromoIsLoading = voucherifyPromoCodesEnabled
        ? voucherifyRemoveIsLoading
        : legacyRemovePromoIsLoading

    const getPaymentIntentRequestPayload = useCallback(() => {
        return {
            user_payment_method_id: selectedPaymentMethod?.id,
        }
    }, [selectedPaymentMethod])

    const clearErrors = () => {
        // A redirect return is a fresh page load, so its failure message must not be wiped by the
        // first clearErrors that runs after mount, or the buyer is left with no idea what happened.
        if (isCheckoutSessionReturn()) return
        setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
        setErrors([])
        setErrorCode(null)
    }

    const { mutateAsync: createGuestAccountAsync } = useMutation(
        (payload) => axiosInstance.post('/ajax/guest-checkout/', payload),
        {
            onSuccess: (data) => {
                const { success, email, show_login, errorMessage, canSendSms } =
                    data.data
                console.log('guest account data', data)
                console.log('success', success)
                console.log('show_login', show_login)
                console.log('showCreditCardForm', showCreditCardForm)

                if (success) {
                    setCreateGuestAccountError(false)
                } else {
                    setCreateGuestAccountError(true)
                }

                if (!showStripe || !success || errors?.length) {
                    setProcessing(false)
                }

                if (!success && show_login) {
                    console.log('showing login')
                    setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
                    const error =
                        'The email used is a registered account, please log in.'
                    setErrors([error])
                    setProcessing(false)

                    handleShowLogin()

                    return [error]
                } else if (!success) {
                    console.log('showing error message')
                    let error =
                        'Something went wrong. Please refresh the page and try again.'
                    if (errorMessage === 'duplicate_phone') {
                        error =
                            'Whoops! That phone number is already in use by another customer. Please, try using a different number.'
                    }
                    if (canSendSms === false) {
                        error =
                            'We are unable to send a text to this number. Landline, VoIP, and International numbers are not supported. Please enter a valid phone number.'
                    }
                    if (errorMessage === 'no_phone') {
                        error =
                            'Whoops! A phone number is required to complete this order.'
                    }
                    setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
                    setErrors([error])
                    setProcessing(false)
                } else {
                    setUserEmail(email)

                    // TriplePixel Contact: guest identity is now known.
                    try {
                        fireTriplePixelContact({
                            email: email,
                            phone: billingDetails?.phone
                                ? convertToTwilioFormat(billingDetails.phone)
                                : null,
                        })
                    } catch (e) {
                        // no-op
                    }

                    pushToDataLayer(
                        [
                            'CheckoutClick',
                            'Checkout',
                            'Click',
                            'ContinueAsGuestSuccess',
                        ],
                        { type: 'stripe' }
                    )
                    setGuestAccountCreated(true)
                    Cookies.set('GCG_CLIENT_USER_ID', `guest-user: ${email}`)
                }
            },
            onError: (error, variables, context) => {
                const errorMessage =
                    'Something went wrong. Please refresh the page and try again.'
                setProcessing(false)
                setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
                setErrors([error.response?.data?.message || errorMessage])

                error.name = ERROR_SIGNING_UP_GUEST_USER
                error.message = error.response?.data?.message || errorMessage
                Sentry.captureException(error, {
                    level: Sentry.Severity.Info,
                    user: {
                        id: JSON.stringify(Cookies.get('GCG_CLIENT_USER_ID')),
                    },
                })
            },
        }
    )

    const handleGuestSignup = useCallback(
        async () => {
            console.log('cb92024a handleGuestSignup called')

            clearErrors()
            try {
                console.log('guest sign up details: ', { billingDetails })

                return await createGuestAccountAsync({
                    email: billingDetails.email,
                    recaptchaToken: null, // This is not enforced on endpoint
                })
            } catch (error) {
                console.error(
                    'Something went wrong handling guest signup: ',
                    error,
                    isPaypalCheckout
                )
                if (isPaypalCheckout) throw error // throw error for handlePaypalOnClick to handle
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [createGuestAccountAsync, billingDetails, isPaypalCheckout]
    )

    const {
        data: cartResponse,
        isLoading: loadingCart,
        error: errorGettingCart,
    } = useFetchCart()

    const fireTriplePixelCheckoutPurchase = useCallback(
        ({ orderId, email, phone, fireContactIfPossible = false } = {}) => {
            const safeOrderId = orderId ? String(orderId).trim() : ''
            if (!safeOrderId) {
                return false
            }

            // Prevent duplicate firing (both within this page and across invoice refreshes).
            try {
                const purchaseKey = `triple_pixel_purchase_${safeOrderId}`
                if (
                    lastTriplePixelPurchaseOrderIdRef.current === safeOrderId ||
                    (window.localStorage && window.localStorage.getItem(purchaseKey))
                ) {
                    return false
                }
            } catch (e) {
                if (lastTriplePixelPurchaseOrderIdRef.current === safeOrderId) {
                    return false
                }
            }

            const cartItems = cartResponse?.cart?.cart_items
            if (!Array.isArray(cartItems) || cartItems.length === 0) {
                return false
            }

            // Expand cart items by quantity so the line-item aggregator can compute totals.
            const expandedGiftCards = []
            cartItems.forEach((cartItem) => {
                const qty = Number(cartItem?.quantity ?? cartItem?.qty ?? 1) || 1
                const merchantName =
                    cartItem?.merchant?.name ||
                    cartItem?.custom_card_details?.wcp_product?.card_network
                const amount = cartItem?.balance ?? cartItem?.amount ?? cartItem?.price

                for (let i = 0; i < qty; i++) {
                    expandedGiftCards.push({ amount, merchantName })
                }
            })

            const lineItems = buildTriplePixelLineItemsFromGiftCards(
                expandedGiftCards
            )
            if (!lineItems.length) {
                return false
            }

            const safeEmail = email ? String(email).trim() : ''
            const safePhone = phone ? String(phone).trim() : ''

            if (fireContactIfPossible) {
                fireTriplePixelContact({
                    email: safeEmail || null,
                    phone: safePhone || null,
                })
            }

            const okPurchase = fireTriplePixelPurchase({
                email: safeEmail || null,
                phone: safePhone || null,
                orderId: safeOrderId,
                lineItems,
            })

            if (okPurchase) {
                lastTriplePixelPurchaseOrderIdRef.current = safeOrderId
                try {
                    const purchaseKey = `triple_pixel_purchase_${safeOrderId}`
                    if (window.localStorage) {
                        window.localStorage.setItem(purchaseKey, '1')
                    }
                } catch (e) {
                    // ignore localStorage errors
                }
            }

            return okPurchase
        },
        [cartResponse]
    )

    const getAppliedPromoCode = useCallback(() => {
        const cachedCart = queryClient.getQueryData('cart')
        const cachedCode = cachedCart?.cart?.promo_code_redemption?.redemption_code

        if (cachedCode) {
            return cachedCode
        }

        return cartResponse?.cart?.promo_code_redemption?.redemption_code || null
    }, [queryClient, cartResponse])

    const {
        data: userAndPaymentMethods,
        isLoading: loadingUserAndPaymentMethods,
        isSuccess: successfullyFetchedUserAndPaymentMethods,
        error: errorGettingUserAndPaymentMethods,
    } = useFetchUserAndPaymentMethods()

    useEffect(() => {
        if (successfullyFetchedUserAndPaymentMethods && userAndPaymentMethods) {
            const { user, paymentMethods } = userAndPaymentMethods
            setUser(user)
            if (
                user.phone_number &&
                (billingDetails.phone === '' ||
                    billingDetails.phone === null) &&
                !loadedPhoneFromUserObject
            ) {
                setBillingDetails({
                    ...billingDetails,
                    phone: maskPhone(user.phone_number),
                })
                setLoadedPhoneFromUserObject(true)
            }
        }
        // eslint-disable-next-line
    }, [
        billingDetails,
        userAndPaymentMethods,
        successfullyFetchedUserAndPaymentMethods,
    ])

    const {
        data: userAllPaymentMethods,
        isSuccess: successfullyFetchedAllUserPaymentMethods,
    } = useFetchUserPaymentMethods()

    useEffect(() => {
        if (successfullyFetchedAllUserPaymentMethods && userAllPaymentMethods) {
            setAnyPaymentMethodsExist(!isEmpty(userAllPaymentMethods))
        }
    }, [userAllPaymentMethods, successfullyFetchedAllUserPaymentMethods])

    const {
        mutate: updateCart,
        isLoading: updatingCart,
        isError: errorUpdatingCart,
        errorMessage: errorUpdatingCartMessage,
    } = useUpdateCart()

    useEffect(() => {
        if (errorUpdatingCart) {
            setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
            setErrors([errorUpdatingCartMessage])
        }
    }, [errorUpdatingCart, errorUpdatingCartMessage])

    const {
        mutate: createOrder,
        error: errorCreatingOrder,
        isError: isErrorCreatingOrder,
        errorMessage: errorCreatingOrderMessage,
        errorCode: errorCodeCreatingOrder,
        reset: resetCreateOrder,
    } = useCreateOrder(CONSUMER_CART_SOURCE)

    const { mutateAsync: createPaypalOrder } = useCreatePaypalOrder()

    const { mutate: logPayPalFailureAuthorization } =
        useLogPaypalFailureAuthorization()


    const resetUpdatePaymentMethodHandler = () => {
        setShowCardError(false)
    }
    const handleOrderOrPaymentFailure = useCallback(
        (errors, errorCode) => {
            console.log('OrderOrPaymentFailure errors:', errors)
            console.log('OrderOrPaymentFailure errorCode:', errorCode)
            setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
            setErrors(errors)
            setErrorCode(errorCode ? errorCode : null)
            setProcessing(false)

            // Refresh the cart data to ensure it reflects the latest state, removing any attached promo codes if applicable.
            //update , we dont want to remove any promos
            //queryClient.invalidateQueries('cart')
           

            // if (
            //     voucherifyPromoCodesEnabled &&
            //     !hasManuallyRemovedVoucherifyPromoCode()
            // ) {
            //      
            //     const storedVoucherifyPromoCode = readVoucherifyPromoCode()
            //     if (storedVoucherifyPromoCode) {
            //      
            //         // Force the auto-apply effect to rerun once the cart refetch completes.
            //         setLastAutoAppliedVoucherifyPromoCode(null)
            //     }
            // }

            // CON-9505: the server releases any authorisation on a failed submit, so what is left for
            // the client is a usable form. The session the buyer just confirmed against is spent, and
            // this gate used to hang off a mutation flag the session path never sets — so nothing
            // happened here and the buyer was left staring at a form that could not be paid.
            //
            // The return parameter has to go first. Session creation refuses to run while it is in the
            // URL — the guard that stops a redirect return from superseding the session it came back to
            // finish — and a 3DS or Amazon Pay return whose order submit then fails still carries it:
            // left in place, the re-create below is silently refused and the payment form never comes
            // back. Once an order attempt has failed, that return is over either way.
            clearCheckoutSessionReturn()
            onCreateCheckoutSession?.()
        },
        [onCreateCheckoutSession]
    )

    useEffect(() => {
        if (isErrorCreatingOrder) {
            const errorData = errorCreatingOrder.response.data
            if (errorData?.error_code === ORDER_CANCELLED) {
                pushToDataLayer([
                    ORDER_CANCELLED,
                    'CheckoutError',
                    'Checkout',
                    'Error',
                    errorData?.cancel_reason,
                ])
                // Redirect to order cancelled page
                const { order_number } = errorData.order
                window.location.href = `${baseUrl}/cart/order/${order_number}/cancelled/`
                return
            } else if (errorData?.error_code === ORDER_CANCELLED_F) {
                pushToDataLayer([
                    ORDER_CANCELLED_F,
                    'CheckoutError',
                    'Checkout',
                    'Error',
                    errorData?.message,
                ])
                // Redirect to order cancelled page
                const { order_number } = errorData.order
                window.location.href = `${baseUrl}/cart/order/${order_number}/cancelled/`
                return
            } else if (errorData?.error_code === DUPLICATE_ORDER) {
                const order_id = errorData.order_id
                window.location.href = `${baseUrl}/cart/order/${order_id}/invoice/`
                return
            } else if (errorData?.error_code === PROMO_CODE_ALREADY_USED) {
                const orderError =
                    'The coupon code provided has already been used by this account. Please remove it and try again.'
                handleOrderOrPaymentFailure(
                    [orderError],
                    errorCodeCreatingOrder ? errorCodeCreatingOrder : null
                )
                resetCreateOrder()
                return
            } else if (errorData?.error_code === AUTH_FAILED) {
                pushToDataLayer([
                    AUTH_FAILED,
                    'CheckoutError',
                    'Checkout',
                    'Error',
                    errorData?.message,
                ])
                const orderError =
                    errorData?.message ??
                    'An error occurred while processing your payment. Please verify and try again.'
                handleOrderOrPaymentFailure(
                    [orderError],
                    errorCodeCreatingOrder ? errorCodeCreatingOrder : null
                )
                resetCreateOrder()
                setSelectedPaymentMethod(null)
                return
            } else if (
                 Array.isArray(errorData?.errors) &&
                 errorData?.errors.includes('An unexpected error occurred while applying your promo code. Please try again.')
            ) {
                resetCreateOrder()
                forgetVoucherifyPromoCode()
                setLastAutoAppliedVoucherifyPromoCode(null)
                queryClient.invalidateQueries('cart')
            }
            const orderError =
                errorCreatingOrderMessage || 'Something went wrong.'
            handleOrderOrPaymentFailure(
                [orderError],
                errorCodeCreatingOrder ? errorCodeCreatingOrder : null
            )
            resetCreateOrder()
        }
    }, [
        baseUrl,
        errorCreatingOrder,
        isErrorCreatingOrder,
        errorCreatingOrderMessage,
        resetCreateOrder,
        setSelectedPaymentMethod,
        errorCodeCreatingOrder,
        handleOrderOrPaymentFailure,
        queryClient
    ])


    const removeCard = useCallback(
        (cartId, cartItemId) => {
            pushToDataLayer([
                'CheckoutClick',
                'Checkout',
                'Click',
                'RemoveCard',
            ])
            updateCart({
                cart_id: cartId,
                cart_items: [
                    {
                        item_id: cartItemId,
                        quantity: 0,
                    },
                ],
            })
        },
        [updateCart]
    )


    const promoErrorMessage = 'Promo code cannot be applied'
    const removePromoErrorMessage =
        'Unable to remove promo code, refresh and try again'

    useEffect(() => {
        const createCartItemsSignature = (items) =>
            items
                .map((item) => {
                    const identifier =
                        item?.item_id ??
                        item?.cart_item_id ??
                        item?.id ??
                        'unknown'
                    const quantity = item?.quantity ?? item?.qty ?? 0

                    return `${identifier}:${quantity}`
                })
                .join('|')

        if (!voucherifyPromoCodesEnabled) {
            cartItemsSignatureRef.current = null
            return
        }

        const cartItems = cartResponse?.cart?.cart_items

        if (!Array.isArray(cartItems)) {
            cartItemsSignatureRef.current = null
            return
        }

        const currentSignature = createCartItemsSignature(cartItems)

        if (hasManuallyRemovedVoucherifyPromoCode()) {
            cartItemsSignatureRef.current = currentSignature
            return
        }

        if (cartItemsSignatureRef.current !== currentSignature) {
            cartItemsSignatureRef.current = currentSignature
            setLastAutoAppliedVoucherifyPromoCode(null)
        }
    }, [voucherifyPromoCodesEnabled, cartResponse?.cart?.cart_items])

    useEffect(() => {
        if (!voucherifyPromoCodesEnabled) {
            forgetVoucherifyPromoCode()
            setLastAutoAppliedVoucherifyPromoCode(null)
            return
        }

        const cartId = cartResponse?.cart?.cart_id
        if (!cartId || voucherifyRemoveIsLoading) {
            return
        }

        const promoWasManuallyRemoved =
            hasManuallyRemovedVoucherifyPromoCode()

        if (promoWasManuallyRemoved) {
            forgetVoucherifyPromoCode()
            if (lastAutoAppliedVoucherifyPromoCode !== null) {
                setLastAutoAppliedVoucherifyPromoCode(null)
            }
            return
        }

        const storedPromoCode = readVoucherifyPromoCode()

        if (!storedPromoCode) {
            if (lastAutoAppliedVoucherifyPromoCode !== null) {
                setLastAutoAppliedVoucherifyPromoCode(null)
            }
            return
        }

        // The preview lives only in the query cache, so any cart refetch wipes it and it must go back.
        const appliedInCart =
            cartResponse?.cart?.promo_code_redemption?.redemption_code

        // React 17 does not batch the cache write and the setState, so the effect can re-enter mid-validate.
        if (
            appliedInCart === storedPromoCode ||
            autoApplyVoucherInFlightRef.current
        ) {
            return
        }

        autoApplyVoucherInFlightRef.current = true

        const autoApplyVoucher = async () => {
            try {
                const payload = {
                    cart_id: cartId,
                    promo_code: storedPromoCode,
                }

                const voucherifyResponse =
                    await validateVoucherifyPromoCode(payload)

                if (!voucherifyResponse?.valid) {
                    throw new Error('Invalid voucherify response')
                }

                applyVoucherifyPreviewToCart(
                    queryClient,
                    storedPromoCode,
                    voucherifyResponse
                )
                rememberVoucherifyPromoCode(storedPromoCode)
            } catch (error) {
                forgetVoucherifyPromoCode()
            } finally {
                autoApplyVoucherInFlightRef.current = false
                setLastAutoAppliedVoucherifyPromoCode(storedPromoCode)
            }
        }

        autoApplyVoucher()
    }, [
        voucherifyPromoCodesEnabled,
        cartResponse,
        validateVoucherifyPromoCode,
        queryClient,
        lastAutoAppliedVoucherifyPromoCode,
        voucherifyRemoveIsLoading,
    ])

    const handleApplyPromoCode = async (promoCode) => {
        const payload = {
            cart_id: cartResponse?.cart?.cart_id,
            promo_code: promoCode,
        }

        removeErrorFromPaths(['applyPromoCode'], errors, setErrors)

        try {
            if (voucherifyPromoCodesEnabled) {
                const voucherifyResponse = await validateVoucherifyPromoCode(
                    payload
                )

                if (!voucherifyResponse?.valid) {
                    const messageSource =
                        voucherifyResponse?.error_message ?? promoErrorMessage
                    const displayMessage =
                        typeof messageSource === 'string'
                            ? messageSource.trim() || promoErrorMessage
                            : promoErrorMessage

                    forgetVoucherifyPromoCode()
                    setLastAutoAppliedVoucherifyPromoCode(null)
                    addFormErrors(
                        displayMessage,
                        ['applyPromoCode'],
                        errors,
                        setErrors
                    )
                    return
                }

                applyVoucherifyPreviewToCart(
                    queryClient,
                    promoCode,
                    voucherifyResponse
                )
                rememberVoucherifyPromoCode(promoCode)
                setLastAutoAppliedVoucherifyPromoCode(promoCode)

            } else {
                const response = await applyPromoCodeLegacy(payload)
                queryClient.setQueryData('cart', response)
                forgetVoucherifyPromoCode()
                setLastAutoAppliedVoucherifyPromoCode(null)

            }
        } catch (error) {
            console.error('Failed to apply promo code', error)
            forgetVoucherifyPromoCode()
            setLastAutoAppliedVoucherifyPromoCode(null)
            const displayMessage = promoErrorMessage
            addFormErrors(
                displayMessage,
                ['applyPromoCode'],
                errors,
                setErrors
            )
        }
    }

    const handleRemovePromoCode = async () => {
        const payload = {
            cart_id: cartResponse?.cart?.cart_id,
        }
        console.log("remove promo")

        removeErrorFromPaths(['removePromoCode'], errors, setErrors)

        if (voucherifyPromoCodesEnabled) {
            setVoucherifyRemoveIsLoading(true)
            try {
                removeVoucherifyPreviewFromCart(queryClient)
                forgetVoucherifyPromoCode()
                markVoucherifyPromoCodeManuallyRemoved()
                setLastAutoAppliedVoucherifyPromoCode(null)
                await queryClient.invalidateQueries('cart')
            } catch (_error) {
                addFormErrors(
                    removePromoErrorMessage,
                    ['removePromoCode'],
                    errors,
                    setErrors
                )
            } finally {
                setVoucherifyRemoveIsLoading(false)
            }

            return
        }

        removePromoCodeLegacy(payload)
            .then((response) => {
                queryClient.setQueryData('cart', response)
                forgetVoucherifyPromoCode()
                setLastAutoAppliedVoucherifyPromoCode(null)
            })
            .catch(() => {
                addFormErrors(
                    'Unable to remove promo code, refresh and try again',
                    ['removePromoCode'],
                    errors,
                    setErrors
                )
            })
    }

    useEffect(() => {
        // For Sentry logging GCG_CLIENT_USER_ID cookie should be set
        const gcgClientUserIdCookie = Cookies.get('GCG_CLIENT_USER_ID')
        const userIsLoggedIn = user?.is_logged_in
        if (
            (userIsLoggedIn && !gcgClientUserIdCookie) ||
            (userIsLoggedIn &&
                gcgClientUserIdCookie !== user?.user_id?.toString())
        ) {
            setUserEmail(user?.email)
            Cookies.set('GCG_CLIENT_USER_ID', user.user_id)
        } else if (!guestAccountCreated) {
            Cookies.set('GCG_CLIENT_USER_ID', `guest-user`)
        }
    }, [user, guestAccountCreated])

    useEffect(() => {
        if (cartResponse && isEmpty(cartResponse.cart?.cart_items))
            pushToDataLayer([
                'CheckoutClick',
                'Checkout',
                'Click',
                'CartEmptied',
            ])
    }, [cartResponse])

    // CON-9505: shared by both modes. On the PaymentIntent path the server has just
    // authorised; on the Checkout Session path the client has, and the backend resolves
    // the PaymentIntent from the session id already stamped on the cart.
    const submitOrder = useCallback(async () => {
        // Get checkout payload to server
        const createOrderPayload = getPaymentIntentRequestPayload()
        // Empty after a redirect return, like the phone below — the stash carries the code that was
        // applied when the buyer confirmed, so the discount they saw is the discount they get.
        const promoCode = getAppliedPromoCode() || readStashedCheckoutPromoCode()
        const orderPayload = {
            ...createOrderPayload,
            device_intelligence_provider_id: deviceId,
            // Empty after a redirect return — the page remounted and state went with it. The email
            // is only consumed server-side when the session user is missing, which is exactly the
            // redirect-return case; on-page flows resolve the user from the session as before.
            phone: convertToTwilioFormat(
                billingDetails.phone || readStashedCheckoutPhone()
            ),
            user_email: billingDetails.email || readStashedCheckoutEmail(),
            cvv: cvv,
        }

        if (promoCode) {
            orderPayload.promo_code = promoCode
        }

        createOrder(
            orderPayload,
            {
                onSuccess: (data) => {
                    const { order_id } = data || {}
                    if (!order_id) {
                        const error = new Error(
                            ERROR_REDIRECTING_TO_ORDER_CONFIRMATION_PAGE
                        )
                        Sentry.captureException(error, {
                            level: Sentry.Severity.Error,
                            user: {
                                id: JSON.stringify(
                                    Cookies.get(
                                        'GCG_CLIENT_USER_ID'
                                    )
                                ),
                            },
                        })
                        return
                    }

                    // TriplePixel events should fire on successful order creation.
                    try {
                        const orderNumber =
                            data?.order_number ||
                            data?.orderNumber ||
                            null
                        const pixelOrderId = orderNumber || order_id

                        const isGuest =
                            !user?.is_logged_in ||
                            showGuestCheckout ||
                            guestAccountCreated
                        // After a redirect return the page remounted, so identity lives only in
                        // the stash — the same values the order payload itself fell back to.
                        const emailForPixel =
                            userEmail ||
                            billingDetails?.email ||
                            readStashedCheckoutEmail() ||
                            null
                        const rawPhoneForPixel =
                            billingDetails?.phone || readStashedCheckoutPhone()
                        const phoneForPixel = rawPhoneForPixel
                            ? convertToTwilioFormat(rawPhoneForPixel)
                            : null

                        fireTriplePixelCheckoutPurchase({
                            orderId: pixelOrderId,
                            email: emailForPixel,
                            phone: phoneForPixel,
                            fireContactIfPossible: isGuest,
                        })

                        if (orderNumber && window.localStorage) {
                            window.localStorage.setItem(
                                `triple_pixel_purchase_${String(order_id)}`,
                                '1'
                            )
                        }
                    } catch (e) {
                        // no-op
                    }

                    forgetVoucherifyPromoCode()
                    setLastAutoAppliedVoucherifyPromoCode(null)

                    // Redirect to order confirmation page
                    window.location.href = `${baseUrl}/cart/order/${order_id}/invoice/`
                },
            }
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        getPaymentIntentRequestPayload,
        getAppliedPromoCode,
        deviceId,
        billingDetails,
        cvv,
        createOrder,
        user,
        showGuestCheckout,
        guestAccountCreated,
        userEmail,
        forgetVoucherifyPromoCode,
        setLastAutoAppliedVoucherifyPromoCode,
        baseUrl,
    ])

    // CON-9505: keep the session's total in step with the cart.
    //
    // This runs through the mounted element's runServerUpdate rather than around it: the SDK caches the
    // session's totals when it initialises, so a server-side change it does not perform itself leaves it
    // confirming against a stale amount. runServerUpdate locks the element, runs the request and refetches,
    // so the amounts repaint in place — superseding the session instead would tear the form down and
    // rebuild it on every promo code, which is what made it flicker.
    //
    // The amount itself is recomputed from the cart server-side; nothing is sent from here.
    const syncedCartFingerprint = useRef(null)

    useEffect(() => {
        const cartItems = cartResponse?.cart?.cart_items

        if (!checkoutClientSecret || !cartItems) {
            return
        }

        const fingerprint = JSON.stringify([cartItems, getAppliedPromoCode()])

        // The first run does NOT get to assume the cart it sees is the cart the session was created
        // from: the server read the cart at the create request, and this effect runs a Stripe round
        // trip later — a quantity edit or the promo auto-apply landing in that window used to be
        // swallowed as the baseline, leaving the element to authorize a stale total. The update is
        // recomputed server-side and idempotent, so the first run simply syncs unconditionally.
        if (syncedCartFingerprint.current === fingerprint) {
            return
        }

        syncedCartFingerprint.current = fingerprint

        const request = () => updateCheckoutSessionAmount()
        const checkout = getActiveCheckout()

        Promise.resolve(
            checkout ? checkout.runServerUpdate(request) : request()
        )
            .then((result) => {
                // runServerUpdate reports its own failures as a result rather than throwing.
                if (result?.type === 'error') {
                    throw new Error(
                        result.error?.message || 'runServerUpdate failed'
                    )
                }
            })
            .catch((error) => {
                Sentry.captureException(error, {
                    level: Sentry.Severity.Error,
                })
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartResponse, checkoutClientSecret])

    // CON-9505: back from a redirect method — Amazon Pay, or a card that needed a 3DS challenge. The
    // session already holds the authorisation, so the only thing left is to submit the order. Without
    // this the buyer is charged and no order is ever created.
    useEffect(() => {
        const returnedSessionId = getCheckoutSessionReturnId()

        if (!returnedSessionId) {
            return undefined
        }

        let cancelled = false
        setProcessing(true)

        getCheckoutSessionPayment(returnedSessionId)
            .then(({ data }) => {
                if (cancelled) {
                    return
                }

                // A failed redirect payment comes back here too, and its session still resolves an
                // intent — just one that was never authorised. Treating the return itself as success
                // would walk an unpaid order all the way to submit, where it could only fail.
                // The failure handler clears the return parameter and creates the fresh session that
                // brings the form back.
                if (!data?.confirmed) {
                    setReturnFailureMessage(
                        'Your payment was not completed. Please choose a payment method and try again.'
                    )
                    handleOrderOrPaymentFailure([])
                    return
                }

                submitOrder()
            })
            .catch((error) => {
                if (cancelled) {
                    return
                }

                Sentry.captureException(error, { level: Sentry.Severity.Error })
                setReturnFailureMessage(
                    'We could not confirm your payment. Please try again.'
                )
                handleOrderOrPaymentFailure([])
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])




    const handleShowCreditCardForm = () => {
        setShowCreditCardForm(true)
        setSelectedPaymentMethod('')
    }

    const handleShowGuestCheckout = () => {
        setPaymentMethodOption(PAYMENT_METHOD.NEW)
        handleShowCreditCardForm()
        clearErrors()
        setShowGuestCheckout(true)
        setBillingDetails({
            ...billingDetails,
            email: guestEmail,
        })
    }

    const handleShowLogin = () => {
        clearErrors()
        setShowGuestCheckout(false)
        setAuthModalIsOpen(true)
    }

    useEffect(() => {
        if (user.is_logged_in === false) {
            setShowGuestCheckout(true)
        }
    }, [user.is_logged_in])

    // The login modal signs a buyer in over AJAX, with no page reload — so the session built before
    // they logged in is still mounted, and it was built for a guest: no customer on it, so their
    // saved cards never appear, and the cart was never bound to them. Recreate it. Superseding
    // releases the old session server-side, and the provider re-keys on the new secret.
    const wasLoggedIn = useRef(null)
    useEffect(() => {
        const isLoggedIn = user?.is_logged_in
        if (typeof isLoggedIn !== 'boolean') {
            return
        }

        if (wasLoggedIn.current === false && isLoggedIn === true) {
            onCreateCheckoutSession?.()
        }

        wasLoggedIn.current = isLoggedIn
    }, [user?.is_logged_in, onCreateCheckoutSession])

    const getUserEmail = useCallback(() => {
        if (guestEmail) {
            return guestEmail
        }

        return user.is_logged_in ? user.username : billingDetails.email
    }, [guestEmail, user, billingDetails])

    const isSelfGiftingCartItem = useCallback(
        (cartItem) => {
            const userEmail = getUserEmail()
            return (
                cartItem.gift_recipient_email &&
                userEmail &&
                cartItem.gift_recipient_email.toLowerCase() ===
                    userEmail.toLowerCase()
            )
        },
        [getUserEmail]
    )

    const isVisaCartItem = useCallback((cartItem) => {
        return (
            has(cartItem.custom_card_details, 'wcp_product.card_network') &&
            cartItem.custom_card_details.wcp_product.card_network === VISA
        )
    }, [])

    const isVirtualCartItem = useCallback((cartItem) => {
        return (
            has(cartItem.custom_card_details, 'wcp_product.virtual_card') &&
            cartItem.custom_card_details.wcp_product.virtual_card
        )
    }, [])

    const getCartHasVirtualVisaWithTotalBalanceMoreThanThreshold =
        useCallback(() => {
            if (!cartResponse) {
                return false
            }
            const cartItems = cartResponse.cart.cart_items
            const totalBalance = cartItems.reduce((acc, cartItem) => {
                if (isVisaCartItem(cartItem) && isVirtualCartItem(cartItem)) {
                    return (acc += cartItem.balance)
                }
                return acc
            }, 0)
            return totalBalance >= VIRTUAL_VISA_GIFTING_THRESHOLD
        }, [cartResponse, isVisaCartItem, isVirtualCartItem])

    const getCartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold =
        useCallback(() => {
            if (!cartResponse) {
                return false
            }
            const cartItems = cartResponse.cart.cart_items
            const totalBalance = cartItems.reduce((acc, cartItem) => {
                if (
                    isSelfGiftingCartItem(cartItem) &&
                    isVisaCartItem(cartItem) &&
                    isVirtualCartItem(cartItem)
                ) {
                    return (acc += cartItem.balance)
                }
                return acc
            }, 0)
            return totalBalance >= VIRTUAL_VISA_SELF_GIFTING_THRESHOLD
        }, [
            cartResponse,
            isSelfGiftingCartItem,
            isVisaCartItem,
            isVirtualCartItem,
        ])

    const getCartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold =
        useCallback(() => {
            if (!cartResponse) {
                return false
            }
            const cartItems = cartResponse.cart.cart_items
            const totalBalance = cartItems.reduce((acc, cartItem) => {
                if (
                    isSelfGiftingCartItem(cartItem) &&
                    cartItem?.plastic === false
                ) {
                    acc = acc + cartItem.balance
                    return acc
                }
                return acc
            }, 0)

            // return true;
            return totalBalance >= VIRTUAL_SELF_GIFTING_THRESHOLD
        }, [cartResponse, isSelfGiftingCartItem])

    useEffect(() => {
        setCartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold(
            getCartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold()
        )
    }, [
        getCartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold,
        setCartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold,
    ])

    useEffect(() => {
        setCartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold(
            getCartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold()
        )
    }, [
        getCartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold,
        setCartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold,
    ])

    useEffect(() => {
        setCartHasVirtualVisaWithTotalBalanceMoreThanThreshold(
            getCartHasVirtualVisaWithTotalBalanceMoreThanThreshold()
        )
    }, [
        setCartHasVirtualVisaWithTotalBalanceMoreThanThreshold,
        getCartHasVirtualVisaWithTotalBalanceMoreThanThreshold,
    ])


    // CON-9505: the single confirm path. The client confirms, and that is what creates and authorises
    // the PaymentIntent, so there is no payment method to hand to the server first — the backend
    // resolves the intent from the session id already stamped on the cart.
    const handleConfirmCheckoutSession = useCallback(async () => {
        const checkout = getActiveCheckout()

        if (!checkout) {
            setProcessing(false)
            return
        }

        // The element authorises the total it cached at init, so a cart edit the fingerprint effect has
        // not synced yet would auth short and the order would be refused at submit.
        let amountSync
        try {
            amountSync = await checkout.runServerUpdate(() =>
                updateCheckoutSessionAmount()
            )
        } catch (error) {
            amountSync = { type: 'error', error }
        }

        if (amountSync?.type === 'error') {
            Sentry.captureException(
                new Error(
                    `Checkout session amount sync failed before confirm: ${
                        amountSync.error?.message || 'unknown'
                    }`
                ),
                { level: Sentry.Severity.Error }
            )
            setProcessing(false)
            setShowCardError(true)
            setErrors([
                'We could not confirm your order total. Please try again.',
            ])
            return
        }

        // Cookie first: it carries the guest's email and is the only source that survives a reload.
        // user.email, not username — both user endpoints omit username for restricted-access guests.
        const email =
            extractEmail(Cookies.get('GCG_CLIENT_USER_ID')) ||
            user?.email ||
            billingDetails.email

        // Survives a redirect method's round trip; order submit falls back to these. Stashed before
        // confirm, because a redirect-based method never comes back to the line after it. The email
        // is what lets submit resolve the buyer at all: the redirect loses the PHP session, and the
        // backend falls back to a lookup by email — exactly how master's Amazon Pay return worked.
        stashCheckoutPhone(billingDetails.phone)
        stashCheckoutEmail(checkout.email || email)
        // The promo the buyer saw in the total. A Voucherify code lives only in the client cart
        // cache, which a redirect empties — without this the return's submit bills full price.
        stashCheckoutPromoCode(getAppliedPromoCode() || '')

        // Set by onRequiresApproval when our own pre-auth check, not the card network, stopped the payment.
        let approvalRefusal = null

        const reportConfirmError = (error) => {
            // TEMP [CON-9510-DEBUG] full error, plus whether we suppress it as element-reported.
            console.log('[CON-9510-DEBUG] reportConfirmError', {
                error,
                type: error?.type ?? null,
                code: error?.code ?? null,
                message: error?.message ?? null,
                suppressedAsElementReported: isSelfReportedByElement(error),
            })

            setProcessing(false)

            // A decline or a bad field is the element's to report, and it puts the message on the
            // field it belongs to. Repeating it in our alert states the same thing twice, the copy
            // furthest from the field the buyer has to fix. A withheld approval has no field to show on.
            if (!approvalRefusal && isSelfReportedByElement(error)) {
                return
            }

            setShowCardError(true)
            setErrors([
                approvalRefusal ||
                    error?.message ||
                    'Payment confirmation failed.',
            ])
            // The alert itself mounts on the next render; the anchor above it is always there.
            scroller.scrollTo('checkoutErrorAlert', { smooth: true, offset: -120 })
        }

        // No billingAddress or phone here, deliberately. The BillingAddressElement writes what the
        // buyer typed onto the session, Stripe puts it on the PaymentMethod at confirm, and order
        // submit mirrors it onto UserPaymentMethod.address — which is where Riskified reads it.
        // Passing values here would *override* what the element collected — and the phone is worse
        // than redundant: Stripe validates it locally (E.164) and GCG's field is masked, so the SDK
        // throws before any network happens. The order payload carries the normalized phone to the
        // server, which is the only place GCG needs it.
        //
        // confirm() can throw as well as resolve to an error — an argument the SDK rejects never
        // reaches the network. Without the catch that throw is unhandled and the processing modal
        // spins forever.
        try {
            const confirmResult = await checkout.confirm({
                // Without this, confirm() always leaves the page. With it, only redirect-based
                // payment methods do, returning to the return_url set at session create.
                redirect: 'if_required',
                // Stripe rejects an email when the session's customer already carries one. A session
                // has no customer for a guest, and for a signed-in buyer with no StripeAccount yet.
                ...(checkout.email ? {} : { email }),
                // Only called for a session created with manual approval.
                onRequiresApproval: async () => {
                    approvalRefusal = await requestCheckoutSessionApproval(
                        checkout.id,
                        false,
                        convertToTwilioFormat(billingDetails.phone || ''),
                    )
                },
            })

            // TEMP [CON-9510-DEBUG] the full resolved result, error or not.
            console.log('[CON-9510-DEBUG] confirmResult', confirmResult)

            if (confirmResult.type === 'error') {
                reportConfirmError(confirmResult.error)
                return
            }
        } catch (error) {
            console.error('checkout.confirm threw', error)
            reportConfirmError(error)
            return
        }

        await submitOrder()
        },
        [
            billingDetails,
            user,
            getAppliedPromoCode,
            setProcessing,
            setShowCardError,
            setErrors,
            submitOrder,
        ]
    )

    const handlePaymentSubmit = useCallback(
        async (event) => {
            // Block native form submission.
            // event?.preventDefault()

            if (!getActiveCheckout()) {
                console.log('checkout session not ready')
                setProcessing(false)
                return
            }

            // One path for every payment option. Saved cards, Apple Pay, Google Pay and Amazon Pay are
            // all rows in the one element, so there is nothing left to branch on here: whatever the
            // buyer picked, the session confirms it.
            await handleConfirmCheckoutSession()
        },
        [handleConfirmCheckoutSession, setProcessing]
    )

    const validateSelfGifting = useCallback(() => {
        // Self Gifting check
        if (getCartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold()) {
            setErrorSelfGifting(true)
            return false
        } else {
            setErrorSelfGifting(false)
            return true
        }
    }, [getCartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold])

    const canPlaceOrder = useCallback(async () => {
        console.log('billing details here', billingDetails)
        clearErrors()
        setReturnFailureMessage('')
        setBillingErrors([])

        if (!validateSelfGifting()) {
            setProcessing(false)
            console.log('not valid self gift')
            return false
        }

        console.log('returning true')

        return true
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingDetails, validateSelfGifting])

    const handlePlaceOrder = useCallback(
        async (event) => {
            console.log('cb92024a handlePlaceOrder called. Context:', {
                showGuestCheckout,
                guestAccountCreated,
                showStripe,
            })
            setProcessing(true)
            // PayPal & Stripe Apple/Google Pay can call guest sign up here
            if (showGuestCheckout && !guestAccountCreated) {
                console.log(
                    'cb92024a handlePlaceOrder calling handleGuestSignup'
                )
                await handleGuestSignup()
            } else {
                console.log(
                    'cb92024a handlePlaceOrder calling handlePaymentSubmit(event)'
                )
                await handlePaymentSubmit(event)
                console.log('handle payment submission')
            }
        },
        [
            showGuestCheckout,
            showStripe,
            guestAccountCreated,
            handleGuestSignup,
            handlePaymentSubmit,
        ]
    )

    /**
     * Return actions.reject() or false to avoid invoking PayPal's createOrder callback
     * @param data
     * @param actions
     * @returns {Promise<boolean>}
     */
    const handlePaypalOnClick = async (data, actions) => {
        setPaymentMethodOption(PAYMENT_METHOD.PAYPAL)
        setSelectedPaymentMethod(PAYMENT_METHOD.PAYPAL)
        setShowCardError(false)
        pushToDataLayer(['CheckoutClick', 'Checkout', 'Click', 'Submit Order'])
    }

    const handlePaypalCreateOrder = async (data, actions) => {
        if (!validateSelfGifting()) {
            setProcessing(false)
            return
        }
        if (errorPhone) {
            setProcessing(false)
            return
        }

        const promoCode = getAppliedPromoCode()

        const createOrderPayload = {
            phone: billingDetails.phone,
        }

        if (promoCode) {
            createOrderPayload.promo_code = promoCode
        }

        return createPaypalOrder(createOrderPayload)
            .then((data) => {
                console.log('createPaypal order', data)
                const { result } = data
                paypalId = data.user_payment_method_id
                return result.id
            })
            .catch((error) => {
                setProcessing(false)
                const { data, status } = error?.response || {}
                if (status && status !== 201 && data?.error) {
                    // Pass blacklisted/user under review/etc error message to PayPal's onError callback to capture with Sentry
                    error.message = data.error
                    // Set error on phone field state when applicable
                    if (data.error && data.error.includes('phone')) {
                        setErrorPhone(data.error)
                        setPaymentMethodOption(PAYMENT_METHOD.NEW)
                        handleShowCreditCardForm()
                    }
                }
                throw error // throw error for handlePaypalOnError to handle
            })
    }

    /**
     * After the user clicks 'Continue' in the PayPal Checkout window, create a GCG order that will also handle creating the PayPal auth
     * @param data
     * @param actions
     */
    const handlePaypalOnApprove = (data, actions) => {
        console.log('handlePaypalOnApprove1', data, actions)
        actions.order
            .authorize()
            .then((authorization) => {
                console.log('paypal authorization complete', authorization)
                // Get the authorization id
                const authorizationID =
                    authorization.purchase_units[0].payments.authorizations[0]
                        .id
                const paypalEmail = guestEmail
                    ? guestEmail
                    : authorization.payer.email_address

                setProcessing(true)

                const promoCode = getAppliedPromoCode()
                const orderPayload = {
                    source: CONSUMER_CART_SOURCE,
                    user_payment_method_id: paypalId, // send stored payment method id for logged in flow
                    paypal_email: paypalEmail,
                    paypal_authorization_id: authorizationID,
                    device_intelligence_provider_id: deviceId,
                    phone: convertToTwilioFormat(billingDetails.phone),
                }

                if (promoCode) {
                    orderPayload.promo_code = promoCode
                }

                createOrder(
                    orderPayload,
                    {
                        onSuccess: (data) => {
                            // Redirect to invoice page
                            const { order_id } = data || {}

                            // TriplePixel events should fire on successful order creation.
                            try {
                                const orderNumber =
                                    data?.order_number || data?.orderNumber || null
                                const pixelOrderId = orderNumber || order_id

                                const isGuest =
                                    !user?.is_logged_in ||
                                    showGuestCheckout ||
                                    guestAccountCreated

                                fireTriplePixelCheckoutPurchase({
                                    orderId: pixelOrderId,
                                    email: paypalEmail,
                                    phone: billingDetails?.phone
                                        ? convertToTwilioFormat(billingDetails.phone)
                                        : null,
                                    fireContactIfPossible: isGuest,
                                })

                                if (orderNumber && window.localStorage) {
                                    window.localStorage.setItem(
                                        `triple_pixel_purchase_${String(order_id)}`,
                                        '1'
                                    )
                                }
                            } catch (e) {
                                // no-op
                            }

                            forgetVoucherifyPromoCode()
                            setLastAutoAppliedVoucherifyPromoCode(null)
                            window.location.href = `${baseUrl}/cart/order/${order_id}/invoice/`
                        },
                    }
                )
            })
            .catch((error) => {
                logPayPalFailureAuthorization({
                    source: CONSUMER_CART_SOURCE,
                    user_payment_method_id: paypalId,
                })
                setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
                setErrors([
                    'Error authorizing your PayPal payment. Please refresh and try again or try a different payment method.',
                ])
                error.name = ERROR_AUTHORIZING_PAYPAL_PAYMENT
                Sentry.captureException(error, {
                    level: Sentry.Severity.Info,

                    user: {
                        id: JSON.stringify(Cookies.get('GCG_CLIENT_USER_ID')),
                    },
                })
                setSelectedPaymentMethod(null)
                setProcessing(false)
            })
    }

    const handlePaypalOnCancel = () => {
        setProcessing(false)
        setSelectedPaymentMethod(null)
        setPaymentMethodOption(PAYMENT_METHOD.NEW)
        handleShowCreditCardForm()
    }

    const handlePaypalOnError = (error) => {
        setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
        if (errorPhone) {
            setErrors(['Please enter a valid phone number.'])
        } else {
            setErrors([
                error?.message ||
                    'Error processing your PayPal payment. Please refresh and try again or try a different payment method.',
            ])
        }
        error.name = ERROR_CREATING_PAYPAL_ORDER
        Sentry.captureException(error, {
            level: Sentry.Severity.Info,
            user: {
                id: JSON.stringify(Cookies.get('GCG_CLIENT_USER_ID')),
            },
        })
        setProcessing(false)
        setSelectedPaymentMethod(null)
        setPaymentMethodOption(PAYMENT_METHOD.NEW)
        handleShowCreditCardForm()
    }

    async function validateCreditCardFormField(path, data) {
        if (user.is_logged_in) {
            return await consumerBillingSchema.validateAt(path, data, {
                abortEarly: false,
            })
        } else if (!isPaypalCheckout) {
            // validate credit card guest checkout. PayPal does not have fields to validate.
            return await consumerGuestCheckoutBillingSchema.validateAt(
                path,
                data,
                {
                    abortEarly: false,
                }
            )
        }
    }

    const validateField = (path, data) => {
        validateCreditCardFormField(path, { [path]: data })
            .then(function () {
                setBillingErrors(removeErrorFromPath(path, billingErrors))
            })
            .catch(function (e) {
                collectFormErrors(e, billingErrors, setBillingErrors)
            })
    }

    if (loadingUserAndPaymentMethods || loadingCart) {
        return <GcgCircularProgress />
    }

    return (
        // The provider sits here rather than above the page, because the page also renders on a
        // redirect return, when there is no session left to mount. With no secret this passes its
        // children straight through.
        <CheckoutSessionElements
            clientSecret={checkoutClientSecret}
            appearance={checkoutAppearance}
        >
        <>
            <BaseAppHelmet
                pageTitle={'Checkout'}
                pageDescription={'Checkout from giftcardgranny.com'}
            />
            <CssBaseline />
            <Layout
                baseUrl={baseUrl}
                brandLogoUrl={brandLogoUrl}
                footerContent={footerContent}
                user={user}
                showGuestCheckout={showGuestCheckout}
            >
                <Grid container className={classes.consumerCheckoutContainer}>
                    {errorGettingUserAndPaymentMethods ? (
                        <Alert
                            severity="error"
                            className={classes.alertMessageContainer}
                            data-testid="error-getting-user-data-alert"
                        >
                            There was a problem getting the user and/or the
                            user's payment methods. Please refresh the page.
                        </Alert>
                    ) : errorGettingCart ? (
                        <Alert
                            severity="error"
                            className={classes.alertMessageContainer}
                            data-testid="error-getting-cart-alert"
                        >
                            There was a problem getting your cart. Please
                            refresh the page.
                        </Alert>
                    ) : !cartResponse ||
                      isEmpty(cartResponse.cart?.cart_items) ? (
                        <EmptyCart baseUrl={baseUrl} />
                    ) : (
                        <>
                            <SimpleBackdrop
                                open={
                                    processing &&
                                    !user?.is_logged_in &&
                                    !showGuestCheckout
                                }
                            />
                            {processing &&
                                (user?.is_logged_in || showGuestCheckout) && (
                                    <ProcessingModal
                                        modalIsOpen={processing}
                                        message={
                                            'Please wait. We are processing your order...'
                                        }
                                    />
                                )}
                            {user?.is_logged_in ? (
                                <>
                                    <Collapse
                                        className={
                                            classes.alertMessageContainer
                                        }
                                        in={showQuantityAdjustmentMessage}
                                    >
                                        <Alert
                                            severity="warning"
                                            action={
                                                <IconButton
                                                    aria-label="Close quantity adjustment message alert"
                                                    color="inherit"
                                                    size="small"
                                                    onClick={() => {
                                                        setShowQuantityAdjustmentMessage(
                                                            false
                                                        )
                                                    }}
                                                >
                                                    <CloseIcon fontSize="inherit" />
                                                </IconButton>
                                            }
                                        >
                                            {quantityAdjustmentMessage}
                                        </Alert>
                                    </Collapse>
                                    {isBlacklisted && (
                                        <Alert
                                            severity="error"
                                            className={
                                                classes.alertMessageContainer
                                            }
                                            data-testid="blacklisted-alert"
                                        >
                                            {blacklistedMessage}
                                        </Alert>
                                    )}

                                    <ConsumerCheckoutForm
                                        setSelectedPaymentElement={
                                            setSelectedPaymentElement
                                        }
                                        selectPaymentElement={
                                            selectedPaymentElement
                                        }
                                        baseUrl={baseUrl}
                                        canPlaceOrder={canPlaceOrder}
                                        isPaypalCheckout={isPaypalCheckout}
                                        handlePaypalCreateOrder={
                                            handlePaypalCreateOrder
                                        }
                                        handlePaypalOnApprove={
                                            handlePaypalOnApprove
                                        }
                                        handlePaypalOnError={
                                            handlePaypalOnError
                                        }
                                        handlePaypalOnCancel={
                                            handlePaypalOnCancel
                                        }
                                        handlePaypalOnClick={
                                            handlePaypalOnClick
                                        }
                                        user={user}
                                        anyPaymentMethodsExist={
                                            anyPaymentMethodsExist
                                        }
                                        billingDetails={billingDetails}
                                        setBillingDetails={setBillingDetails}
                                        cvv={cvv}
                                        setCvv={setCvv}
                                        options={options}
                                        validateField={validateField}
                                        errors={errors}
                                        setErrors={setErrors}
                                        errorCode={errorCode}
                                        billingErrors={billingErrors}
                                        processing={processing}
                                        setProcessing={setProcessing}
                                        showCreditCardForm={showCreditCardForm}
                                        showPhoneInput={showPhoneInput}
                                        showPlaceOrderButton={
                                            showPlaceOrderButton
                                        }
                                        handleShowCreditCardForm={
                                            handleShowCreditCardForm
                                        }
                                        handlePlaceOrder={handlePlaceOrder}
                                        setBillingErrors={setBillingErrors}
                                        savePaymentMethod={savePaymentMethod}
                                        setSavePaymentMethod={
                                            setSavePaymentMethod
                                        }
                                        onPaymentFormLoadError={
                                            onPaymentFormLoadError
                                        }
                                        checkoutSessionReady={Boolean(
                                            checkoutClientSecret
                                        )}
                                        checkoutSessionLoadFailed={
                                            checkoutSessionLoadFailed
                                        }
                                        resetUpdatePaymentMethodErrors={
                                            resetUpdatePaymentMethodHandler
                                        }
                                        cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold={
                                            cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold
                                        }
                                        errorSelfGifting={errorSelfGifting}
                                        errorPhone={errorPhone}
                                        errorCvv={errorCvv}
                                        setErrorCvv={setErrorCvv}
                                        setErrorPhone={setErrorPhone}
                                        checkingPhone={checkingPhone}
                                        setCheckingPhone={setCheckingPhone}
                                        phoneCheckSuccess={phoneCheckSuccess}
                                        setPhoneCheckSuccess={
                                            setPhoneCheckSuccess
                                        }
                                        showStripe={showStripe}
                                        clearErrors={clearErrors}
                                        handleErrorAlertClick={
                                            handleErrorAlertClick
                                        }
                                        showCardError={showCardError}
                                        setShowCardError={setShowCardError}
                                        returnFailureMessage={returnFailureMessage}
                                        onDismissReturnFailure={() => {
                                            setReturnFailureMessage('')
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <Collapse
                                        className={
                                            classes.alertMessageContainer
                                        }
                                        in={showQuantityAdjustmentMessage}
                                    >
                                        <Alert
                                            severity="warning"
                                            action={
                                                <IconButton
                                                    aria-label="Close quantity adjustment message alert"
                                                    color="inherit"
                                                    size="small"
                                                    onClick={() => {
                                                        setShowQuantityAdjustmentMessage(
                                                            false
                                                        )
                                                    }}
                                                >
                                                    <CloseIcon fontSize="inherit" />
                                                </IconButton>
                                            }
                                        >
                                            {quantityAdjustmentMessage}
                                        </Alert>
                                    </Collapse>
                                    {isBlacklisted && (
                                        <Alert
                                            severity="error"
                                            className={
                                                classes.alertMessageContainer
                                            }
                                        >
                                            {blacklistedMessage}
                                        </Alert>
                                    )}
                                    <ConsumerGuestCheckoutForm
                                        setSelectedPaymentElement={
                                            setSelectedPaymentElement
                                        }
                                        baseUrl={baseUrl}
                                        handleShowLogin={handleShowLogin}
                                        setCreateGuestAccountError={
                                            setCreateGuestAccountError
                                        }
                                        createGuestAccountError={
                                            createGuestAccountError
                                        }
                                        canPlaceOrder={canPlaceOrder}
                                        isPaypalCheckout={isPaypalCheckout}
                                        handlePaypalOnClick={
                                            handlePaypalOnClick
                                        }
                                        handlePaypalCreateOrder={
                                            handlePaypalCreateOrder
                                        }
                                        handlePaypalOnApprove={
                                            handlePaypalOnApprove
                                        }
                                        handlePaypalOnError={
                                            handlePaypalOnError
                                        }
                                        handlePaypalOnCancel={
                                            handlePaypalOnCancel
                                        }
                                        showCreditCardForm={showCreditCardForm}
                                        showPhoneInput={showPhoneInput}
                                        showPlaceOrderButton={
                                            showPlaceOrderButton
                                        }
                                        billingDetails={billingDetails}
                                        setBillingDetails={setBillingDetails}
                                        cvv={cvv}
                                        setCvv={setCvv}
                                        options={options}
                                        errors={errors}
                                        setErrors={setErrors}
                                        errorCode={errorCode}
                                        billingErrors={billingErrors}
                                        processing={processing}
                                        setProcessing={setProcessing}
                                        validateField={validateField}
                                        handlePlaceOrder={handlePlaceOrder}
                                        onPaymentFormLoadError={
                                            onPaymentFormLoadError
                                        }
                                        checkoutSessionReady={Boolean(
                                            checkoutClientSecret
                                        )}
                                        checkoutSessionLoadFailed={
                                            checkoutSessionLoadFailed
                                        }
                                        resetUpdatePaymentMethodErrors={
                                            resetUpdatePaymentMethodHandler
                                        }
                                        cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold={
                                            cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold
                                        }
                                        cartHasVirtualVisaWithTotalBalanceMoreThanThreshold={
                                            cartHasVirtualVisaWithTotalBalanceMoreThanThreshold
                                        }
                                        errorSelfGifting={errorSelfGifting}
                                        errorPhone={errorPhone}
                                        errorCvv={errorCvv}
                                        handleGuestSignup={handleGuestSignup}
                                        setBillingErrors={setBillingErrors}
                                        setErrorPhone={setErrorPhone}
                                        setErrorCvv={setErrorCvv}
                                        checkingPhone={checkingPhone}
                                        setCheckingPhone={setCheckingPhone}
                                        phoneCheckSuccess={phoneCheckSuccess}
                                        setPhoneCheckSuccess={
                                            setPhoneCheckSuccess
                                        }
                                        clearErrors={clearErrors}
                                        showStripe={showStripe}
                                        handleErrorAlertClick={
                                            handleErrorAlertClick
                                        }
                                        showCardError={showCardError}
                                        setShowCardError={setShowCardError}
                                        returnFailureMessage={returnFailureMessage}
                                        onDismissReturnFailure={() => {
                                            setReturnFailureMessage('')
                                        }}
                                    />
                                    <ConsumerCheckoutLoginCreateAccountModal
                                        modalIsOpen={authModalIsOpen}
                                        closeModal={() => {
                                            clearErrors()
                                            setShowGuestCheckout(true)
                                            setAuthModalIsOpen(false)
                                        }}
                                        baseUrl={baseUrl}
                                        facebookLoginUrl={facebookLoginUrl}
                                        googleLoginUrl={googleLoginUrl}
                                        loginFormErrors={loginFormErrors}
                                        signupFormErrors={signupFormErrors}
                                        processing={processing}
                                        errors={errors}
                                        setErrors={setErrors}
                                        handleShowGuestCheckout={
                                            handleShowGuestCheckout
                                        }
                                        guestEmail={guestEmail}
                                        setGuestEmail={setGuestEmail}
                                        cartHasVirtualVisaWithTotalBalanceMoreThanThreshold={
                                            cartHasVirtualVisaWithTotalBalanceMoreThanThreshold
                                        }
                                        setProcessing={setProcessing}
                                        setShowCardError={setShowCardError}
                                        setSignupFormErrors={
                                            setSignupFormErrors
                                        }
                                        clearErrors={clearErrors}
                                        setLoginFormErrors={setLoginFormErrors}
                                        cartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold={
                                            cartHasSelfGiftingVirtualWithTotalBalanceMoreThanThreshold
                                        }
                                        recaptchaEnabled={recaptchaEnabled}
                                        recaptchaSiteKey={recaptchaSiteKey}
                                    />
                                </>
                            )}
                            <Grid
                                item
                                xs={12}
                                md={6}
                                className={classes.orderSummaryContainer}
                            >
                                {updatingCart && (
                                    <div
                                        className={
                                            classes.orderSummaryContainerLoading
                                        }
                                    >
                                        <LargeGcgCircularProgress />
                                    </div>
                                )}
                                <OrderSummary
                                    user={user}
                                    cart={cartResponse.cart}
                                    applyPromoIsLoading={applyPromoIsLoading}
                                    removePromoIsLoading={removePromoIsLoading}
                                    errors={errors}
                                    removeCard={removeCard}
                                    handleApplyPromoCode={handleApplyPromoCode}
                                    handleRemovePromoCode={
                                        handleRemovePromoCode
                                    }
                                />
                                <Grid
                                    item
                                    container
                                    spacing={2}
                                    className={
                                        classes.nortonAndBbbLogosContainer
                                    }
                                >
                                    <Grid item xs={12}>
                                        <a
                                            href="https://www.bbb.org/us/pa/pittsburgh/profile/gift-cards/gift-card-granny-0141-71025047/#sealclick"
                                            target="_blank"
                                            rel="noreferrer noopener"
                                        >
                                            <img
                                                className={classes.bbbLogo}
                                                loading="lazy"
                                                src="https://seal-westernpennsylvania.bbb.org/seals/blue-seal-120-61-bbb-71025047.png"
                                                alt="Gift Card Granny BBB Business Review"
                                            />
                                        </a>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Layout>
        </>
        </CheckoutSessionElements>
    )
}

export default (props) => <ConsumerCheckoutPage {...props} />
